import React, { useEffect, useState } from "react";

import Carousel from "./Carousel";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  CircularProgress,
  Typography,
  Toolbar,
  AppBar,
  IconButton,
} from "@material-ui/core";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteIcon from "@material-ui/icons/Delete";
import PdfViewer from "./PdfViewer";
import { getFileExtension, isFileTypeImage } from "./FileColumnRenderer";
import ImageLoader from "./ImageLoader";
import ReactPlayer from "react-player";
import Papa from "papaparse";
import styled from "styled-components";

const useStyles = makeStyles({
  title: {
    flexGrow: 1,
  },
  backDrop: {
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    opacity: 1,
  },
  color: {
    color: "white !important",
  },
});

function FileViewer(props) {
  const classes = useStyles();
  const { onClose, open, startIndex = 0 } = props;
  const [files, setFiles] = useState(props.files);
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  let [isChanged, setIsChanged] = useState(false);

  const currentFile = files[currentIndex];
  const handleClose = (forceSave = false) => {
    if (isChanged) {
      props.onChange(files);
    }
    onClose();
  };

  const handleChange = (index) => {
    console.log(index);
    setCurrentIndex(index);
  };
  const handleDelete = () => {
    const index = files.findIndex((file) => file.path == currentFile.path);
    const newFiles = [...files];

    newFiles.splice(index, 1);
    setIsChanged(true);
    setFiles(newFiles);
    setCurrentIndex(index > 0 ? index - 1 : 0);
    props.onChange(newFiles);
    onClose();
  };

  return (
    <Dialog
      fullScreen
      onClose={handleClose}
      open={open}
      PaperProps={{
        style: {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
      BackdropProps={{
        classes: {
          root: classes.backDrop,
        },
      }}
    >
      <div className={classes.appBar}>
        <AppBar position="static" style={{ backgroundColor: "transparent" }}>
          <Toolbar
            variant="dense"
            onClick={(e) => e.currentTarget == e.target && handleClose()}
          >
            <IconButton edge="start" onClick={handleClose} color="inherit">
              <ArrowBackIcon className={classes.color} />
            </IconButton>
            <Typography className={classes.color} variant="h6">
              {currentFile && currentFile.name}
            </Typography>
            <div className={classes.title} onClick={handleClose}></div>
            <DeleteButton onClick={handleDelete} custProps={props} />
            <DownloadButton file={currentFile} />
          </Toolbar>
        </AppBar>
      </div>
      {
        <FilesCarousel
          items={files}
          startIndex={startIndex}
          onChange={handleChange}
          onOutsideClick={handleClose}
        />
      }
    </Dialog>
  );
}

function FilesCarousel({ items, startIndex, onChange, onOutsideClick }) {
  return (
    <Carousel
      autoPlay={false}
      navButtonsAlwaysVisible={true}
      startAt={startIndex}
      next={onChange}
      prev={onChange}
      indicators={false}
      onOutsideClick={onOutsideClick}
    >
      {items.map((item) => (
        <File key={item.filepath} file={item} onOutsideClick={onOutsideClick} />
      ))}
    </Carousel>
  );
}

const CSVViewerContainer = styled.div`
  background-color: white;
  border-radius: 5px;
  padding: 8px;
  width: 90%;
  max-height: 85vh;
  overflow: auto;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Arial, sans-serif;
  font-size: 14px;
`;

const StyledTh = styled.th`
  border: 1px solid #ddd;
  padding: 12px;
  text-align: left;
  font-weight: bold;
  color: #333;
`;

const StyledTd = styled.td`
  border: 1px solid #ddd;
  padding: 12px;
  text-align: left;
  color: #555;
`;

const StyledPre = styled.pre`
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 20px;
`;

function FilePathFormat(file) {
  let fileFullPath = file.path;
  if (fileFullPath) {
    fileFullPath = fileFullPath.includes("http://tigersheet.s3.amazonaws.com") ? fileFullPath.replace("http", "https") : fileFullPath;
  } else if (file.filepath) {
    let serverPath = "https://tigersheet.s3.amazonaws.com/";
    fileFullPath = serverPath.concat(file.filepath);
  } else if (file.fullpath) {
    fileFullPath = file.fullpath.includes("https") ? file.fullpath : file.fullpath.includes("http") ? file.fullpath.replace("http", "https") : file.fullpath;
  }
  return fileFullPath;
}

export function CsvViewer({ file, ext }) {
  const [data, setData] = useState([]);
  let csvFileFullPath = FilePathFormat(file);
  const errorItem = (
    <div
      style={{
        color: "white",
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <span
        title={file.name}
        className={"fiv-sqo fiv-icon-blank fiv-icon-" + ext}
        style={{ width: 50, height: 50, lineHeight: "inherit" }}
      ></span>
      File View Not Supported ({file.name}) <DownloadButton file={file} />
    </div>
  );

  try {
    useEffect(() => {
      fetch(csvFileFullPath)
        .then((response) => response.text())
        .then((text) => {
          const parsed = Papa.parse(text, { header: false, skipEmptyLines: true });
          setData(parsed.data);
        })
        .catch((error) => console.error("Error loading CSV:", error));
    }, [csvFileFullPath]);
  
    if(!csvFileFullPath) {
      return errorItem;
    }

    if (!data || data.length === 0) {
      return (<CircularProgress size={100} />);
    }

    const headers = data[0];
    const rows = data.slice(1);

    return (
      <CSVViewerContainer>
        <StyledTable>
          <thead>
            <tr style={{ backgroundColor: "#f4f4f4" }}>
              {headers.map((header, index) => (
                <StyledTh key={index}>
                  {header}
                </StyledTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? "#fafafa" : "#ffffff",
                }}
              >
                {row.map((cell, cellIndex) => (
                  <StyledTd key={cellIndex}>
                    {cell}
                  </StyledTd>
                ))}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </CSVViewerContainer>
    );
  } catch(e) {
    return errorItem;
  }
}

export function TxtViewer({ file, ext }) {
  const [content, setContent] = useState("");
  let txtFileFullPath = FilePathFormat(file);
  const errorItem = (
    <div
      style={{
        color: "white",
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <span
        title={file.name}
        className={"fiv-sqo fiv-icon-blank fiv-icon-" + ext}
        style={{ width: 50, height: 50, lineHeight: "inherit" }}
      ></span>
      File View Not Supported ({file.name}) <DownloadButton file={file} />
    </div>
  );

  try {
    useEffect(() => {
      fetch(txtFileFullPath)
        .then((response) => response.text())
        .then((text) => setContent(text))
        .catch((error) => console.error("Error loading text file:", error));
    }, [txtFileFullPath]);

    if(!txtFileFullPath) {
      return errorItem;
    }

    return (
      <div style={{ backgroundColor: "white", width: "90%", maxHeight: "85vh", overflow: "auto"}}>
        <StyledPre>{content}</StyledPre>
      </div>
    );
  } catch(e) {
    return errorItem;
  }
}
function File({ file, onOutsideClick }) {
  const ext = getFileExtension(file);
  let item;
  const errorItem = (
    <div
      style={{
        color: "white",
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <span
        title={file.name}
        className={"fiv-sqo fiv-icon-blank fiv-icon-" + ext}
        style={{ width: 50, height: 50, lineHeight: "inherit" }}
      ></span>
      File View Not Supported ({file.name}) <DownloadButton file={file} />
    </div>
  );

  if (ext === "pdf") {
    let fileFullPath = file.fullpath;
    if (file.fullpath?.includes("http://tigersheet.s3.amazonaws.com")) {
      fileFullPath = file.fullpath.replace("http", "https");
    }
    item = <PdfViewer file={fileFullPath} />;
  } else if (isFileTypeImage(ext)) {
    item = (
      <ImageLoader
        src={file.fullpath}
        style={{ maxWidth: "100vw", maxHeight: "90vh" }}
        loaderComponent={<CircularProgress size={100} />}
      />
    );
  } else if (isFileTypeVideo(ext)) {
    item = (
      <ReactPlayer
        playing
        controls={true}
        url={file.fullpath}
        width="80%"
        height="80%"
        stopOnUnmount={false}
        pip={false}
        config={{
          file: {
            attributes: {
              disablePictureInPicture: true,
              controlsList: "nofullscreen",
            },
          },
        }}
      />
    );
  } else if (ext === "csv") {
    item = (
      <CsvViewer file={file} ext={ext} />
    );
  } else if (ext === "txt") {
    item = (
      <TxtViewer file={file} ext={ext} />
    );
  } else if (ext === "mp3") {
    try {
      let audioFileFullPath = FilePathFormat(file);

      if(!audioFileFullPath) {
        item = errorItem;
      } else {
        item = (
          <audio controls style={{ width: "50%" }}>
            <source src={audioFileFullPath} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        );
      }
    } catch (e) {
      item = errorItem;
    }
  } else if (["doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(ext)) {
    try {
      let DocFileFullPath = FilePathFormat(file);

      if(!DocFileFullPath) {
        item = errorItem;
      } else {
        item = (
          <iframe
            src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(DocFileFullPath)}`}
            style={{ width: "90%", height: "95%" }}
            title={file.name}
          />
        );
      }
    } catch (e) {
      item = errorItem;
    }
  } else {
    item = errorItem;
  }

  const handleClick = (e) => {
    if (e.target == e.currentTarget) {
      onOutsideClick();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "90vh",
      }}
      onClick={handleClick}
    >
      {item}
    </div>
  );
}


function DeleteButton({ onClick, custProps }) {
  const classes = useStyles();

  const handleDelete = async () => {
    try {
      let editPermissionData = custProps.hasEditPermissions();
      if (Number(custProps.nodeData?.row_id) && !editPermissionData[0]) {
        const message = editPermissionData[1];
        window.appStore.notificationStore.showNotification(
          message,
          "caution"
        );
        return;
      }
      
      if(custProps.isReadOnly){
        window.appStore.notificationStore.showNotification(
          "This is a non-editable cell, you can't delete this file",
          "caution"
        );
        return ;
      }
      await window.appStore.notificationStore.confirm({
        description: <>Are you sure you wish to delete this file?</>,
      });

      onClick();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <IconButton
      onClick={handleDelete}
      edge="start"
      color="inherit"
    >
      <DeleteIcon className={classes.color} />
    </IconButton>
  );
}

function DownloadButton({ file }) {
  const classes = useStyles();
  if (!file) {
    return null;
  }
  return (
    <IconButton
      component="a"
      download={file.name}
      href={file.fullpath}
      edge="start"
      color="inherit"
    >
      <GetAppIcon className={classes.color} />
    </IconButton>
  );
}

export function isFileTypeVideo(ext) {
  return [
    "mpg",
    "mpeg",
    "avi",
    "wmv",
    "mov",
    "rm",
    "ram",
    "swf",
    "flv",
    "ogg",
    "webm",
    "mp4",
  ].includes(ext.toLowerCase());
}

export default FileViewer;